// Shared by the browser and the build: one source for canonicals and public facts.
const organization = require('../content/organization.json');
const articles = require('../content/actualites.json');
const primaryPages = require('./publicPages.json');
const secondaryPages = require('./secondaryPages.json');

const ORIGIN = organization.url.replace(/\/$/, '');
const aliases = { '/presentation-rcds': '/rcds-batico' };
const secondary = secondaryPages.map(([path, heading, description]) => ({
  path, heading, description, title: `${heading} | O'SCOP`,
  paragraphs: [description], links: [["Contacter l'équipe", '/contact']],
}));
const pages = [...primaryPages, ...secondary];
const pageMap = new Map(pages.map(page => [page.path, page]));
const articleMap = new Map(articles.map(article => [`/actualites/${article.slug}`, article]));

function normalizePath(value = '/') {
  const pathname = String(value).split(/[?#]/)[0].replace(/^\/proxy\/\d+(?=\/|$)/, '') || '/';
  return pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
}

function getPage(value) {
  const requestedPath = normalizePath(value);
  const path = aliases[requestedPath] || requestedPath;
  const page = pageMap.get(path);
  if (page) return { ...page, requestedPath, indexable: true };
  const article = articleMap.get(path);
  if (article) return {
    path, requestedPath, title: `${article.titre} | O'SCOP`, heading: article.titre,
    description: article.description, type: 'Article', article, indexable: true,
    paragraphs: [article.intro], links: article.liens.map(link => [link.label, link.to]),
  };
  // Sponsor profiles are public, but their live API data must not be invented here.
  if (/^\/partenaires-sponsors\/[a-zA-Z0-9_-]+$/.test(path)) return {
    path, requestedPath, title: "Fiche partenaire ou sponsor | O'SCOP",
    heading: 'Fiche partenaire ou sponsor',
    description: "Consultez la fiche publiée de ce partenaire ou sponsor sur O'SCOP.",
    indexable: true, dynamic: true,
  };
  return {
    path, requestedPath, title: "Espace O'SCOP",
    heading: "Espace O'SCOP", description: "Accédez à votre espace et à vos démarches O'SCOP.",
    indexable: false,
  };
}

function getStructuredData(page) {
  if (!page.indexable) return null;
  const url = `${ORIGIN}${page.path}`;
  const organizationId = `${ORIGIN}/#organization`;
  const websiteId = `${ORIGIN}/#website`;
  const address = {
    '@type': 'PostalAddress', addressLocality: organization.locality,
    postalCode: organization.postalCode, addressRegion: organization.region,
    addressCountry: organization.countryCode,
  };
  if (organization.streetAddress) address.streetAddress = organization.streetAddress;
  const graph = [
    {
      '@type': 'Organization', '@id': organizationId, name: organization.name,
      legalName: organization.legalName, alternateName: organization.alternateName,
      url: ORIGIN, logo: `${ORIGIN}/oscop-logo-new.png`,
      identifier: { '@type': 'PropertyValue', propertyID: 'SIREN', value: organization.siren },
      email: organization.contactEmail, telephone: organization.phone, address,
    },
    {
      '@type': 'WebSite', '@id': websiteId, name: "O'SCOP — Objectif Scop Outremer",
      url: `${ORIGIN}/`, inLanguage: 'fr-FR', publisher: { '@id': organizationId },
    },
    {
      '@type': page.type === 'Article' ? 'WebPage' : (page.type || 'WebPage'),
      '@id': `${url}#webpage`, url, name: page.heading, description: page.description,
      inLanguage: 'fr-FR', isPartOf: { '@id': websiteId },
      publisher: { '@id': organizationId }, about: { '@id': organizationId },
    },
  ];
  if (page.article) graph.push({
    '@type': 'Article', '@id': `${url}#article`, headline: page.article.titre,
    description: page.description, inLanguage: 'fr-FR',
    mainEntityOfPage: { '@id': `${url}#webpage` },
    author: { '@id': organizationId }, publisher: { '@id': organizationId },
    articleSection: page.article.categorie,
    // No invented publication date, review, score or accreditation.
  });
  if (page.path !== '/') graph.push({
    '@type': 'BreadcrumbList', '@id': `${url}#breadcrumbs`,
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: `${ORIGIN}/` },
      ...(page.article ? [{ '@type': 'ListItem', position: 2, name: 'Ressources', item: `${ORIGIN}/actualites` }] : []),
      { '@type': 'ListItem', position: page.article ? 3 : 2, name: page.heading, item: url },
    ],
  });
  return { '@context': 'https://schema.org', '@graph': graph };
}

function getMetadata(value) {
  const page = getPage(value);
  return {
    page, title: page.title, description: page.description,
    canonical: page.indexable ? `${ORIGIN}${page.path}` : null,
    robots: page.indexable ? 'index, follow, max-image-preview:large' : 'noindex, nofollow',
    structuredData: getStructuredData(page),
    image: `${ORIGIN}/oscop-logo-new.png`,
  };
}

function publicPaths() {
  return [...pages.map(page => page.path), ...articleMap.keys()];
}

module.exports = { ORIGIN, organization, articles, aliases, normalizePath, getPage, getMetadata, publicPaths };
